<template>
    <div class="ftu-overlay" role="dialog" aria-modal="true" aria-labelledby="ftu-title">
        <div class="ftu-card">
            <!-- Order-is-safe banner. Present in every state: whatever happens to this
                 add-on, the prescription the customer just paid for is untouched. -->
            <div class="ftu-confirmed">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span>Your order is confirmed</span>
            </div>

            <!-- Success — the charge went through -->
            <template v-if="purchased">
                <h2 id="ftu-title" class="ftu-title">Priority review added</h2>
                <p class="ftu-copy">
                    Your intake is now at the front of the review queue. We charged
                    {{ formattedFee }} once to your card — your prescription order is unchanged.
                </p>
                <button type="button" class="ftu-btn ftu-btn--primary" @click="$emit('close')">
                    Continue
                </button>
            </template>

            <!-- Offer -->
            <template v-else>
                <h2 id="ftu-title" class="ftu-title">Fast-Track My Physician Review?</h2>
                <p class="ftu-copy">
                    Skip the standard review queue. Your medical intake is moved to the front of
                    the line and reviewed by a licensed physician on a priority basis, so your
                    medication ships sooner. Standard review is always included free.
                </p>

                <div class="ftu-price-row">
                    <span class="ftu-price-label">Priority Physician Review</span>
                    <span class="ftu-price-amount">
                        <span class="ftu-price">{{ formattedFee }}</span>
                        <span class="ftu-price-note">Billed once</span>
                    </span>
                </div>

                <!-- Card fields. Hidden until the customer has accepted the offer,
                     then slid into view. Klarna reveals them on the first press, a
                     wallet only after its own card was refused. -->
                <transition name="ftu-reveal">
                    <div v-show="showCard" class="ftu-card-reveal">
                        <p v-if="cardNote" class="ftu-note">{{ cardNote }}</p>

                        <div class="ftu-card-fields">
                            <label class="ftu-field-label">Card number</label>
                            <div id="ftu-card-number" class="ftu-field"></div>
                            <div class="ftu-field-row">
                                <div class="ftu-field-col">
                                    <label class="ftu-field-label">Expiration</label>
                                    <div id="ftu-card-expiry" class="ftu-field"></div>
                                </div>
                                <div class="ftu-field-col">
                                    <label class="ftu-field-label">CVC</label>
                                    <div id="ftu-card-cvc" class="ftu-field"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </transition>

                <p v-if="errorMessage" class="ftu-error">{{ errorMessage }}</p>

                <button
                    type="button"
                    class="ftu-btn ftu-btn--primary"
                    :disabled="busy"
                    @click="accept"
                >
                    <span v-if="busy" class="ftu-spinner" aria-hidden="true"></span>
                    <span>{{ acceptLabel }}</span>
                </button>

                <button
                    type="button"
                    class="ftu-btn ftu-btn--ghost"
                    :disabled="busy"
                    @click="decline"
                >
                    No thanks, continue
                </button>
            </template>
        </div>
    </div>
</template>

<script>
import { loadStripe } from '@stripe/stripe-js';
import { chargeFastTrackUpsell, fireFastTrackUpsellPurchase } from '@/utils/fastTrackUpsell';

/** Klarna authorizes one fixed amount up front and leaves no card on file. */
const collectsCardUpFront = (paymentMethod) => paymentMethod === 'klarna';

/**
 * Apple Pay, Google Pay and Link all post a real Stripe PaymentMethod to
 * /api/checkout-stripe exactly as a typed card does, so there IS a card on file to
 * bill. These orders try it first and only reveal fields if the backend cannot
 * re-bill it — asking a wallet customer to type a card before even attempting the
 * charge throws away the one-tap purchase the wallet exists for.
 */
const canFallBackToCard = (paymentMethod) =>
    paymentMethod === 'apple_pay' || paymentMethod === 'google_pay' || paymentMethod === 'link';

const paymentMethodLabel = (paymentMethod) => {
    if (paymentMethod === 'apple_pay') return 'Apple Pay';
    if (paymentMethod === 'google_pay') return 'Google Pay';
    if (paymentMethod === 'link') return 'Link';
    return 'Klarna';
};

export default {
    name: 'FastTrackUpsellModal',

    props: {
        // Resolved { product, plan } from loadFastTrackAddon. The parent only mounts
        // this component when the add-on resolved, so it is never null here.
        addon: {
            type: Object,
            required: true,
        },
        // How the order was paid: card | apple_pay | google_pay | link | klarna.
        paymentMethod: {
            type: String,
            default: 'card',
        },
        baseUrl: {
            type: String,
            default: '',
        },
        userId: {
            type: String,
            default: '',
        },
        firstName: {
            type: String,
            default: '',
        },
        lastName: {
            type: String,
            default: '',
        },
        email: {
            type: String,
            default: '',
        },
        shippingAddress: {
            type: Object,
            default: null,
        },
        trackingSessionId: {
            type: String,
            default: '',
        },
        ipAddress: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            busy: false,
            purchased: false,
            errorMessage: '',
            showCard: false,
            stripe: null,
            elements: null,
            cardNumberElement: null,
            cardExpiryElement: null,
            cardCvcElement: null,
        };
    },

    computed: {
        fee() {
            return Number(Number(this.addon.plan.price).toFixed(2));
        },

        formattedFee() {
            return `$${this.fee.toFixed(2)}`;
        },

        acceptLabel() {
            if (this.busy) return 'Adding...';
            // Once the card fields are open the customer has already said yes, so
            // the button stops asking and names the charge it is about to make.
            if (this.showCard) return `Pay ${this.formattedFee} and add`;
            return `Yes, add for ${this.formattedFee}`;
        },

        cardNote() {
            // Nothing about a card is said before the customer accepts. The offer
            // reads as a second charge if it opens talking about payment.
            if (!this.showCard) return '';
            if (canFallBackToCard(this.paymentMethod)) {
                return `We couldn't charge the card from your ${paymentMethodLabel(this.paymentMethod)} order for this add-on. Enter a card below to add it.`;
            }
            if (collectsCardUpFront(this.paymentMethod)) {
                return `One-time ${this.formattedFee} charge. Klarna cannot be used for add-ons, so this is billed to a card you enter below. Your Klarna plan is not changed.`;
            }
            return '';
        },
    },

    async mounted() {
        // Klarna will need a card typed, but not yet. Fetch Stripe now so the
        // fields appear the moment the offer is accepted, with no stall.
        if (collectsCardUpFront(this.paymentMethod)) {
            await this.loadStripeOnce();
        }
    },

    beforeDestroy() {
        this.destroyCardFields();
    },

    methods: {
        decline() {
            if (this.busy) return;
            this.$emit('close');
        },

        async accept() {
            if (this.busy) return;

            // Klarna leaves no card on file, so one has to be typed. The first
            // press is the customer saying yes and reveals the fields; the second
            // one charges. Showing a card form to someone who has not agreed to
            // the add-on reads as a surprise charge and loses the offer.
            if (collectsCardUpFront(this.paymentMethod) && !this.showCard) {
                this.errorMessage = '';
                this.showCard = true;
                await this.$nextTick();
                await this.mountCardFields();
                return;
            }

            this.errorMessage = '';
            this.busy = true;

            try {
                let card = null;

                if (this.showCard) {
                    card = await this.tokenizeCard();
                    if (!card) {
                        this.busy = false;
                        return;
                    }
                }

                const result = await chargeFastTrackUpsell({
                    baseUrl: this.baseUrl,
                    userId: this.userId,
                    addon: this.addon,
                    firstName: this.firstName,
                    lastName: this.lastName,
                    email: this.email,
                    shippingAddress: this.shippingAddress,
                    trackingSessionId: this.trackingSessionId,
                    ipAddress: this.ipAddress,
                    card,
                });

                if (result.ok) {
                    fireFastTrackUpsellPurchase(this.addon, result);
                    this.destroyCardFields();
                    this.purchased = true;
                    this.busy = false;
                    return;
                }

                // A wallet order whose card could not be re-billed gets one chance to
                // enter a card instead of a dead end. Every other path just reports it.
                if (canFallBackToCard(this.paymentMethod) && !this.showCard) {
                    this.showCard = true;
                    await this.$nextTick();
                    await this.mountCardFields();
                    this.errorMessage = '';
                } else {
                    this.errorMessage = result.message;
                }
                this.busy = false;
            } catch (error) {
                console.error('[fast-track] priority review could not be added:', error);
                this.errorMessage = 'We could not add priority review. Your order is still confirmed.';
                this.busy = false;
            }
        },

        async loadStripeOnce() {
            if (this.stripe) return;
            try {
                this.stripe = await loadStripe(process.env.VUE_APP_STRIPE_KEY);
            } catch (error) {
                console.error('[fast-track] Stripe could not be loaded:', error);
            }
        },

        async mountCardFields() {
            try {
                await this.loadStripeOnce();
                if (!this.stripe) {
                    this.errorMessage = 'Payment form failed to load. Your order is still confirmed.';
                    return;
                }
                if (!this.elements) {
                    this.elements = this.stripe.elements();
                }
                if (this.cardNumberElement) return;

                const style = {
                    base: {
                        fontSize: '15px',
                        fontWeight: 'normal',
                        color: '#000',
                        '::placeholder': { color: '#9ca3af', fontWeight: 'normal' },
                    },
                };

                this.cardNumberElement = this.elements.create('cardNumber', {
                    style,
                    placeholder: '1234 1234 1234 1234',
                    showIcon: false,
                });
                this.cardNumberElement.mount('#ftu-card-number');

                this.cardExpiryElement = this.elements.create('cardExpiry', { style, placeholder: 'MM / YY' });
                this.cardExpiryElement.mount('#ftu-card-expiry');

                this.cardCvcElement = this.elements.create('cardCvc', { style, placeholder: 'CVC' });
                this.cardCvcElement.mount('#ftu-card-cvc');
            } catch (error) {
                console.error('[fast-track] Stripe card fields failed to mount:', error);
                this.errorMessage = 'Payment form failed to load. Your order is still confirmed.';
            }
        },

        destroyCardFields() {
            try {
                if (this.cardNumberElement) { this.cardNumberElement.destroy(); this.cardNumberElement = null; }
                if (this.cardExpiryElement) { this.cardExpiryElement.destroy(); this.cardExpiryElement = null; }
                if (this.cardCvcElement) { this.cardCvcElement.destroy(); this.cardCvcElement = null; }
            } catch (error) {
                console.warn('[fast-track] Stripe cleanup failed', error);
            }
        },

        /** Returns { token } or null after setting errorMessage. Never throws. */
        async tokenizeCard() {
            if (!this.stripe || !this.cardNumberElement) {
                this.errorMessage = 'Payment form is not ready. Please try again.';
                return null;
            }

            const billingDetails = {
                name: `${this.firstName || ''} ${this.lastName || ''}`.trim(),
                email: this.email || '',
            };

            const { paymentMethod, error } = await this.stripe.createPaymentMethod({
                type: 'card',
                card: this.cardNumberElement,
                billing_details: billingDetails,
            });

            if (error || !paymentMethod) {
                this.errorMessage = (error && error.message)
                    || 'Invalid card details. Please check your card information.';
                return null;
            }

            return { token: paymentMethod.id };
        },
    },
};
</script>

<style scoped>
/* Every class is prefixed ftu- so a scoped rule can never outspecify a webflow class
   that happens to share a name. Light-only surface: no [data-theme="dark"] block,
   the dashboard's dark theme persists across SPA routes into these checkouts and
   would black out the card. Colours come from the theme variables in
   rx-pros.webflow.css, with the literal only ever as the var fallback. */
.ftu-overlay {
    position: fixed;
    inset: 0;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    /* Above the checkouts' own error_popup (9999) and processing overlay. */
    z-index: 100000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    background: rgba(0, 0, 0, 0.55);
    overflow-y: auto;
}

.ftu-card {
    box-sizing: border-box;
    width: 100%;
    max-width: 440px;
    margin: auto;
    padding: 24px;
    background: var(--white, #fff);
    border-radius: 14px;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
    font-family: var(--nunito, Nunito, sans-serif);
    text-align: left;
}

.ftu-confirmed {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 14px;
    color: var(--darkbluetext, #30989d);
    font-size: 13px;
    font-weight: 700;
    line-height: 1.2;
}

.ftu-title {
    margin: 0 0 10px;
    color: var(--primary-black, #242424);
    font-size: 22px;
    font-weight: 900;
    line-height: 1.2;
}

.ftu-copy {
    margin: 0 0 16px;
    color: var(--light-black-07, #242424b3);
    font-size: 14px;
    font-weight: 400;
    line-height: 1.45;
}

.ftu-price-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 14px;
    border: 1px solid var(--df-grey, #dfdfdf);
    border-radius: 10px;
    background: var(--f9-grey, #f9f9f9);
}

.ftu-price-label {
    color: var(--primary-black, #242424);
    font-size: 14px;
    font-weight: 700;
    line-height: 1.3;
}

.ftu-price-amount {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
}

.ftu-price {
    color: var(--dark-bluegreen, #4e7882);
    font-size: 18px;
    font-weight: 800;
    line-height: 1.2;
    white-space: nowrap;
}

.ftu-price-note {
    color: var(--999-grey, #999);
    font-size: 12px;
    line-height: 1.3;
    white-space: nowrap;
}

.ftu-note {
    margin: 12px 0 0;
    color: var(--666-grey, #666);
    font-size: 12px;
    font-weight: 400;
    line-height: 1.4;
}

.ftu-card-fields {
    margin-top: 14px;
}

/* The card fields drop in after the offer is accepted. max-height carries the
   height because the block has none until Stripe's iframes measure themselves;
   420px clears the tallest state (note + three fields) at every breakpoint. */
.ftu-reveal-enter-active {
    overflow: hidden;
    transition: max-height 0.32s ease, opacity 0.24s ease 0.06s, transform 0.32s ease;
}

.ftu-reveal-enter {
    max-height: 0;
    opacity: 0;
    transform: translateY(-8px);
}

.ftu-reveal-enter-to {
    max-height: 420px;
    opacity: 1;
    transform: translateY(0);
}

.ftu-field-label {
    display: block;
    margin-bottom: 5px;
    color: var(--primary-black, #242424);
    font-size: 12px;
    font-weight: 700;
    line-height: 1.2;
}

.ftu-field {
    padding: 11px 12px;
    border: 1px solid var(--df-grey, #dfdfdf);
    border-radius: 8px;
    background: var(--white, #fff);
}

.ftu-field-row {
    display: flex;
    gap: 10px;
    margin-top: 10px;
}

.ftu-field-col {
    flex: 1;
    min-width: 0;
}

.ftu-error {
    margin: 12px 0 0;
    color: #dc2626;
    font-size: 13px;
    font-weight: 400;
    line-height: 1.4;
}

.ftu-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    margin-top: 14px;
    padding: 14px 18px;
    border: none;
    border-radius: 10px;
    font-family: inherit;
    font-size: 16px;
    font-weight: 800;
    line-height: 1.2;
    cursor: pointer;
    transition: opacity 0.15s ease;
}

.ftu-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.ftu-btn--primary {
    background: var(--dark-bluegreen, #4e7882);
    color: var(--white, #fff);
}

.ftu-btn--ghost {
    margin-top: 8px;
    background: transparent;
    color: var(--666-grey, #666);
    font-size: 14px;
    font-weight: 400;
    text-decoration: underline;
}

@media (hover: hover) and (pointer: fine) {
    .ftu-btn:not(:disabled):hover {
        opacity: 0.9;
    }
}

.ftu-spinner {
    width: 15px;
    height: 15px;
    border: 2px solid rgba(255, 255, 255, 0.4);
    border-top-color: var(--white, #fff);
    border-radius: 50%;
    animation: ftu-spin 0.7s linear infinite;
}

@keyframes ftu-spin {
    to { transform: rotate(360deg); }
}

@media screen and (max-width: 479px) {
    .ftu-card {
        padding: 20px 16px;
    }

    .ftu-title {
        font-size: 20px;
    }
}
</style>
